// @flow

import React, { Component } from "react";
import { isEqual, get, isNumber, noop } from "lodash";
import type {
  RaceTypeCodeEnum,
  RaceCardBettingInterest
} from "@tvg/types/Race";
import type { FullResultRunner } from "@fdr/types/PastPerformance";
import formatSequential from "@tvg/formatter/numeric";
import {
  ResultsList,
  AllRunnersButton,
  SeeAllRunnersSection,
  OtherInfo,
  Scratches,
  ScratchesLabel,
  Number,
  RunnerResultsContainer,
  FinishPosition
} from "./styled-components";

import RunnerNumber from "../../_atoms/RunnerNumber";
import RaceResultsHeader from "../../_molecules/RaceResultsHeader";
import RunnerLine from "../../_molecules/RunnerLine";

type Props = {
  runnerName: string,
  runners: FullResultRunner[],
  raceTypeCode: RaceTypeCodeEnum,
  resultsType: "basic" | "full",
  scratchedRunners: string[],
  showSilks: boolean,
  enableNewRacePage: boolean,
  bettingInterests?: Array<Array<RaceCardBettingInterest>>,
  isRunnerExpandable: boolean,
  selectAllButtonLabel?: string
};

type State = {
  isExpanded: boolean
};

export default class RaceResults extends Component<Props, State> {
  static defaultProps = {
    runnerName: "",
    runners: [],
    scratchedRunners: [],
    raceTypeCode: "T",
    resultsType: "full",
    showSilks: false,
    enableNewRacePage: false,
    bettingInterests: [],
    isRunnerExpandable: false,
    selectAllButtonLabel: "Box"
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      isExpanded: false
    };
  }

  getSnapshotBeforeUpdate(prevProps: Props) {
    if (!isEqual(this.props, prevProps) && this.state.isExpanded === true) {
      this.setState({ isExpanded: false });
    }
  }

  getRunnersForFullResults = (
    runners: FullResultRunner[],
    runnerName: string
  ): FullResultRunner[] =>
    runners.reduce((resultRunner, runner, index) => {
      if (
        index < 3 ||
        runner.horseName.toLowerCase() === runnerName.toLowerCase()
      ) {
        resultRunner.push(runner);
      }
      return resultRunner;
    }, []);

  formatPosition = (position: number): string =>
    position !== 99 ? formatSequential(position) : "DNF";

  render() {
    const {
      runners,
      runnerName,
      raceTypeCode,
      resultsType,
      scratchedRunners,
      showSilks,
      enableNewRacePage
    } = this.props;

    const hasRunners = runners.length > 0;
    const hasTimeformData = resultsType === "full";
    const runnersToDisplay =
      this.state.isExpanded || !hasTimeformData
        ? runners
        : this.getRunnersForFullResults(runners, runnerName);

    const runnerBI = runnersToDisplay.find(
      (runner) => runner.horseName === runnerName
    );

    const runnerBIindex = runnersToDisplay.findIndex(
      (runner) => runner.horseName === runnerName
    );

    return (
      hasRunners && (
        <section data-qa-label="raceResults">
          <ResultsList>
            <RaceResultsHeader
              data-qa-label="raceResults-header"
              isGreyhound={raceTypeCode === "G"}
              hasWin={false}
              hasPlace={false}
              hasShow={false}
              simpleVersion
              hasDistance={hasTimeformData}
              hasOdds={hasTimeformData}
            />
            {runnersToDisplay.map((runner: FullResultRunner, index: number) => (
              <RunnerResultsContainer
                isHighlighted={runner.horseName === runnerName}
              >
                <FinishPosition>
                  {(runner.horseName !== runnerName ||
                    isNumber(runner.finishPosition) ||
                    this.state.isExpanded) &&
                    this.formatPosition(runner.finishPosition)}
                </FinishPosition>
                <RunnerLine
                  key={`${runner.runnerId}-results-position-${runner.finishPosition}`}
                  runner={runner}
                  hasDashedBorder={
                    runnerBIindex !== -1 &&
                    index === runnerBIindex - 1 &&
                    get(runnerBI, "finishPosition") > 3 &&
                    !this.state.isExpanded
                  }
                  hasMarginLeft={false}
                  bettingInterest={{ odds: runner.currentOdds }}
                  raceTypeCode={raceTypeCode}
                  isWagerable={false}
                  showWinProb={false}
                  isHighlighted={runner.horseName === runnerName}
                  showSilks={showSilks}
                  enableNewRacePage={enableNewRacePage}
                  bettingInterests={this.props.bettingInterests}
                  isExpandable={this.props.isRunnerExpandable}
                  onRaceTagsInfoClick={noop}
                  selectAllButtonLabel={this.props.selectAllButtonLabel}
                />
              </RunnerResultsContainer>
            ))}
          </ResultsList>
          <OtherInfo>
            <Scratches>
              <ScratchesLabel>Scratches</ScratchesLabel>
              {scratchedRunners.length === 0
                ? "No Changes"
                : scratchedRunners.map((runner) => (
                    <Number key={runner}>
                      <RunnerNumber
                        key={`${runner}-number`}
                        runnerId={runner}
                        raceTypeCode={this.props.raceTypeCode}
                      />
                    </Number>
                  ))}
            </Scratches>
          </OtherInfo>
          {!this.state.isExpanded && hasTimeformData && (
            <SeeAllRunnersSection>
              <AllRunnersButton
                size="big"
                isStretched
                type="secondary"
                onClick={() => {
                  this.setState({ isExpanded: true });
                }}
                qaLabel="hpp-all-runners"
              >
                See all runners
              </AllRunnersButton>
            </SeeAllRunnersSection>
          )}
        </section>
      )
    );
  }
}
